import React, {PureComponent} from 'react';
import {func} from 'prop-types';
import invoke from 'lodash.invoke';
import SearchResultItem from '../SearchResultItem';
import SearchResultsEmpty from '../SearchResultsEmpty';
import List from '../list/List';
import GroupLabel from '../list/GroupLabel';
import SearchResultsPagination from '../SearchResultsPagination';
import {guid} from '@ultradent/utilities/Guid';
import {FixedSizeList} from 'react-window';

class SearchResults extends PureComponent {
    constructor () {
        super();

        this.renderedResultGroups = this.renderedResultGroups.bind( this );
        this.renderPagedResults = this.renderPagedResults.bind( this );
        this.renderResultItem = this.renderResultItem.bind( this );
    }

    renderPagedResults ( {pagedResults} ) {
        const {groupLabelClass, groupResultsClass} = this.props;
        const {groupName, renderer, virtualized, itemSize} = pagedResults?.[0]?.queryGroup;

        return (
            <List data-testid="search-results-list" className={groupResultsClass}>
                {groupName && <GroupLabel className={groupLabelClass}>{groupName}</GroupLabel>}
                {virtualized
                    ? <FixedSizeList
                        width="100%"
                        height={itemSize * pagedResults.length}
                        itemCount={pagedResults.length}
                        itemSize={itemSize}>
                        {this.renderVirtualResultItem( {
                            results: pagedResults, renderer
                        } )}
                    </FixedSizeList>
                    : pagedResults.map( this.renderResultItem( {renderer} ) )}
            </List>
        )
    }

    createSearchResultItem ( result, RendererComponent, style = null ) {
        const {
            resultItemClass, activeClass, search: {
                itemProps, selectedIndex, handleResultHover, handleResultClick
            }
        } = this.props;

        return (
            <SearchResultItem
                index={result.index}
                key={`SearchResultItem-${guid()}`}
                onResultHover={handleResultHover}
                onResultClick={handleResultClick}
                style={style}
                className={resultItemClass}
                activeClass={activeClass}
                active={selectedIndex === result.index}>
                <RendererComponent {...result} {...itemProps} />
            </SearchResultItem>
        );
    }

    renderResultItem ( {renderer} ) {
        const RendererComponent = renderer;

        return ( result ) => this.createSearchResultItem( result, RendererComponent );
    }

    renderVirtualResultItem ( {results, renderer} ) {
        const RendererComponent = renderer;

        return ( {index, style} ) => {
            const result = results[index];

            return result && this.createSearchResultItem( result, RendererComponent, style );
        };
    }

    renderedResultGroups () {
        const {
            className, search: {
                pageIndex, showPagination, resultGroups, query, queryGroups, onPageIndexChange, onVisibleResultsChange
            }
        } = this.props;

        let index = 0;
        return queryGroups.map( ( queryGroup, i ) => {
            // predicate to determine if queryGroup should be hidden
            if ( invoke( queryGroup, 'hideIf', query, resultGroups ) ) {
                return null;
            }

            const group = resultGroups[i];
            const indexedResults = group.results.map( item => {
                const lineResult = {
                    index: index, queryGroup, ...item
                };
                index++;
                return lineResult;
            } );

            return (<SearchResultsPagination
                className={className}
                groupIndex={i}
                key={`SearchResultsPagination-${guid()}`}
                onResultsChange={onVisibleResultsChange}
                showPagination={showPagination}
                query={query}
                results={indexedResults}
                pageIndex={group.pageIndex}
                onPageIndexChange={onPageIndexChange}
                resultsPerPage={group.resultLimit}>
                {this.renderPagedResults}
            </SearchResultsPagination>);
        } )
    }

    render () {
        const {
            noResultsClass, search: {
                loading, error, isActive, alwaysShowResults, showNoResults, hasResults
            }
        } = this.props;

        if ( error || (!isActive && !alwaysShowResults) ) {
            return null;
        }

        if ( !hasResults ) {
            return showNoResults ?
                this.props.children( <SearchResultsEmpty loading={loading} className={noResultsClass}/> ) :
                null;
        }

        return this.props.children( this.renderedResultGroups() );
    }
}

SearchResults.propTypes = {
    children: func.isRequired
}

export default SearchResults;
